import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { tr, enUS, de, ar, es, fr, it, type Locale } from "date-fns/locale";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  MessageSquare,
  PenLine,
  Search,
  Ticket,
  UserRound,
} from "lucide-react";

import { cn } from "@/lib/utils";
import useAuthStore from "@/store/use-auth-store";
import { Button } from "@/components/ui/button";

import {
  useMyTickets,
  useTicketStats,
  type TicketStatusFilter,
} from "../hooks/use-tickets";
import { TicketStatusBadge } from "../components/TicketStatusBadge";
import { TICKET_STATUS_COLORS } from "../config/status-colors";
import { isOwnTicket } from "../utils/message-author";
import type { SupportTicket, SupportTicketStats } from "../types";

const DATE_LOCALES: Record<string, Locale> = { tr, en: enUS, de, ar, es, fr, it };

/** Filtrelenebilir statüler — renk/i18n bilgisi status-colors config'inden */
const STATUS_FILTERS: Exclude<TicketStatusFilter, "all">[] = [
  "open",
  "in_progress",
  "on_hold",
  "solved",
  "closed",
];

/** "Tümü" çipi bir statü olmadığı için rengi burada tanımlı */
const ALL_CHIP_COLORS = {
  dot: "bg-indigo-500",
  activeChip:
    "border-indigo-500/50 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
};

/** Bu statülerde destek artık cevap borçlu değildir */
const NO_REPLY_NEEDED_STATUSES = new Set(["solved", "closed", "resolved"]);

const FilterChip: React.FC<{
  active: boolean;
  label: string;
  count: number;
  dotClassName: string;
  activeClassName: string;
  onClick: () => void;
}> = ({ active, label, count, dotClassName, activeClassName, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
      active
        ? activeClassName
        : "border-border bg-card text-muted-foreground hover:border-border hover:bg-muted/60 hover:text-foreground",
    )}
  >
    <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dotClassName)} />
    {label}
    <span className="text-[10px] font-semibold tabular-nums opacity-70">
      {count}
    </span>
  </button>
);

/** Metadata satırlarını atlayarak önizleme metni üretir */
const getPreviewText = (ticket: SupportTicket): string => {
  const lastMessage = ticket.messages?.[ticket.messages.length - 1];
  const raw = lastMessage?.body ?? ticket.message ?? "";
  const lines = raw
    .split(/\r?\n/)
    .filter((line) => line.trim() && !/^\[[^\]]+\]$/.test(line.trim()));
  return lines.join(" ").trim();
};

const TicketRow: React.FC<{
  ticket: SupportTicket;
  dateLocale: Locale;
  /** Admin görünümünde cevap bekleyen ticket'lar vurgulanır */
  highlightUnread?: boolean;
}> = ({ ticket, dateLocale, highlightUnread }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();

  // Detaydan geri dönüşte admin aynı sekmeye (kapsama) dönebilsin
  const scopeSuffix = searchParams.get("scope") === "all" ? "?scope=all" : "";

  const messageCount = ticket.messages?.length ?? 0;
  const preview = getPreviewText(ticket);
  // Admin panelde herkesin ticket'ını görür — başkasına ait satırlarda
  // talep sahibi belirtilir
  const showRequester = !isOwnTicket(ticket, user);

  // Backend'in "unread" tanımıyla aynı kural: çözülmemiş/kapanmamış ve
  // son mesaj kullanıcıdan → destek hâlâ cevap borçlu
  const lastMessage = ticket.messages?.[ticket.messages.length - 1];
  const needsReply =
    !!highlightUnread &&
    !!lastMessage &&
    !lastMessage.isFromSupport &&
    !NO_REPLY_NEEDED_STATUSES.has(ticket.status);

  return (
    <Link
      to={`/support/tickets/${ticket._id}${scopeSuffix}`}
      className={cn(
        "flex w-full flex-col gap-1.5 px-4 py-3 text-left transition-colors sm:gap-2 sm:px-5 sm:py-4",
        "hover:bg-muted/50 dark:hover:bg-muted/30",
        needsReply && "bg-indigo-500/[0.04] dark:bg-indigo-500/[0.07]",
      )}
    >
      <div className="flex w-full items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
            {needsReply && (
              <span
                title={t("support.myTickets.awaitingReply")}
                className="h-2 w-2 shrink-0 rounded-full bg-indigo-500"
              />
            )}
            <span className="truncate">
              {ticket.subject || t("support.myTickets.untitled")}
            </span>
          </span>
          {preview && (
            <span className="line-clamp-1 text-sm text-muted-foreground">
              {preview}
            </span>
          )}
        </div>
        <TicketStatusBadge status={ticket.status} />
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {typeof ticket.ticketNumber === "number" && (
          <span className="font-medium">#{ticket.ticketNumber}</span>
        )}
        {showRequester && (
          <span className="inline-flex max-w-[220px] items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-medium text-foreground/80">
            <UserRound className="h-3 w-3 shrink-0" />
            <span className="truncate">{ticket.fullName || ticket.email}</span>
          </span>
        )}
        {messageCount > 0 && (
          <span className="inline-flex items-center gap-1">
            <MessageSquare className="h-3 w-3" />
            {messageCount}
          </span>
        )}
        <span>
          {formatDistanceToNow(new Date(ticket.updatedAt), {
            addSuffix: true,
            locale: dateLocale,
          })}
        </span>
      </div>
    </Link>
  );
};

/**
 * "My Tickets" — kullanıcının destek talepleri listesi.
 * Satıra tıklayınca /support/tickets/:id detay sayfasına gidilir.
 */
export const MyTicketsSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const {
    tickets,
    meta,
    page,
    setPage,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    isAllScope,
    isLoading,
    isError,
    refetch,
  } = useMyTickets();
  const { stats } = useTicketStats();

  const listRef = useRef<HTMLDivElement>(null);

  // Sayfa değişince liste kaydırması başa döner
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [page]);

  // Login olmayan kullanıcıya geçmiş talepler gösterilemez
  if (!user?._id) return null;

  const dateLocale =
    DATE_LOCALES[i18n.language?.split("-")[0] ?? "en"] ?? enUS;

  const hasActiveFilter = statusFilter !== "all" || search.trim().length > 0;
  const showToolbar =
    !isLoading && !isError && (tickets.length > 0 || hasActiveFilter);

  const statusCount = (
    ticketStats: SupportTicketStats,
    value: string,
  ): number =>
    ticketStats.byStatus[value as keyof SupportTicketStats["byStatus"]] ?? 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2.5 sm:gap-4">
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-foreground sm:text-base">
          {isAllScope
            ? t("support.myTickets.allTitle")
            : t("support.myTickets.title")}
        </h2>
        {/* Admin (tüm talepler): cevap bekleyen talep sayısı */}
        {isAllScope && stats && stats.unread > 0 && (
          <span className="rounded-full bg-indigo-500/10 px-2 py-0.5 text-[11px] font-semibold text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400">
            {t("support.myTickets.unreadCount", { count: stats.unread })}
          </span>
        )}

        {/* Arama — başlıkla aynı hizada, sağa yaslı */}
        {showToolbar && (
          <div className="relative ml-auto shrink-0">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("support.myTickets.searchPlaceholder")}
              className="h-8 w-full rounded-lg border border-border bg-card pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-indigo-500/50 focus:outline-none sm:w-56"
            />
          </div>
        )}
      </div>

      {/* Statü filtreleri (admin, tüm talepler) */}
      {showToolbar && isAllScope && stats && (
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          <FilterChip
            active={statusFilter === "all"}
            label={t("common.all")}
            count={stats.total}
            dotClassName={ALL_CHIP_COLORS.dot}
            activeClassName={ALL_CHIP_COLORS.activeChip}
            onClick={() => setStatusFilter("all")}
          />
          {STATUS_FILTERS.map((value) => {
            const count = statusCount(stats, value);
            // Boş statüler çip kirliliği yaratmasın; aktif filtre hep kalsın
            if (count === 0 && statusFilter !== value) return null;
            const colors = TICKET_STATUS_COLORS[value];
            return (
              <FilterChip
                key={value}
                active={statusFilter === value}
                label={t(colors.i18nKey)}
                count={count}
                dotClassName={colors.dot}
                activeClassName={colors.activeChip}
                onClick={() => setStatusFilter(value)}
              />
            );
          })}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center rounded-2xl border border-border bg-card py-20 shadow-sm">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
          <p className="text-sm text-muted-foreground">
            {t("support.myTickets.loadError")}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="dark:border-border dark:bg-muted/50 dark:text-foreground dark:hover:bg-muted"
          >
            {t("common.retry")}
          </Button>
        </div>
      ) : tickets.length === 0 && hasActiveFilter ? (
        // Filtre/arama sonuç vermedi — genel boş durumla karıştırılmasın
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card px-6 py-14 text-center shadow-sm">
          <Search className="h-5 w-5 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t("support.myTickets.noResults")}
          </p>
        </div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center gap-5 rounded-2xl border border-border bg-card px-6 py-14 text-center shadow-sm sm:p-8">
          <div className="relative flex h-16 w-16 shrink-0 items-center justify-center">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-500/15 to-violet-500/10 dark:from-indigo-500/25 dark:to-violet-500/15" />
            <Ticket className="relative h-7 w-7 text-indigo-600 dark:text-indigo-400" />
          </div>

          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-semibold text-foreground">
              {t("support.myTickets.emptyTitle")}
            </p>
            <p className="max-w-sm text-sm text-muted-foreground">
              {t("support.myTickets.emptyDescription")}
            </p>
          </div>

          <Button asChild variant="outline" size="sm" className="gap-1.5">
            <Link to="/support">
              <PenLine className="h-3.5 w-3.5" />
              {t("support.myTickets.createNewCta")}
            </Link>
          </Button>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          {/* Kartın tek kayan bölümü — sayfa değil liste kayar */}
          <div
            ref={listRef}
            className="flex min-h-0 flex-1 flex-col divide-y divide-border overflow-y-auto"
          >
            {tickets.map((ticket) => (
              <TicketRow
                key={ticket._id}
                ticket={ticket}
                dateLocale={dateLocale}
                highlightUnread={isAllScope}
              />
            ))}
          </div>

          {/* Sayfalama — kaydırma dışında sabit kalır */}
          {meta && meta.totalPage > 1 && (
            <div className="flex shrink-0 items-center justify-between border-t border-border px-4 py-2.5 sm:px-5 sm:py-3">
              <span className="text-xs text-muted-foreground">
                {t("support.myTickets.pageInfo", {
                  page: meta.page,
                  totalPage: meta.totalPage,
                })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="h-8 w-8 p-0 dark:border-border dark:bg-muted/50 dark:text-foreground dark:hover:bg-muted"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= meta.totalPage}
                  onClick={() => setPage(page + 1)}
                  className="h-8 w-8 p-0 dark:border-border dark:bg-muted/50 dark:text-foreground dark:hover:bg-muted"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
